import type { CSSProperties } from "react";
import type { ChatWallpaper } from "@/types/(my-space)/chat.interface";

/**
 * Chat backgrounds. Keys are the API's ChatWallpaper values (data — never
 * translated). The colours mirror the mobile app's chat_wallpapers.dart so a
 * chat looks the same on both; change them together.
 *
 * `default` draws nothing here — the room keeps its standard token background.
 */
export interface IChatWallpaperSpec {
  key: ChatWallpaper;
  style?: CSSProperties;
  dark?: boolean;
}

const solid = (color: string): CSSProperties => ({ backgroundColor: color });
const gradient = (angle: number, ...stops: string[]): CSSProperties => ({
  backgroundImage: `linear-gradient(${angle}deg, ${stops.join(", ")})`,
});
const dots = (base: string, dot: string, spacing = 18): CSSProperties => ({
  backgroundColor: base,
  backgroundImage: `radial-gradient(${dot} 1.2px, transparent 1.4px)`,
  backgroundSize: `${spacing}px ${spacing}px`,
});
const grid = (base: string, line: string, spacing = 24): CSSProperties => ({
  backgroundColor: base,
  backgroundImage: `linear-gradient(${line} 1px, transparent 1px), linear-gradient(90deg, ${line} 1px, transparent 1px)`,
  backgroundSize: `${spacing}px ${spacing}px`,
});

export const CHAT_WALLPAPERS: IChatWallpaperSpec[] = [
  { key: "default" },
  { key: "paper", style: solid("#F7F3EA") },
  { key: "mint", style: solid("#E3F4EC") },
  { key: "sky", style: solid("#E2EEFB") },
  { key: "lavender", style: solid("#ECE7FA") },
  { key: "blush", style: solid("#FBE8EC") },
  { key: "sand", style: solid("#F3E6D3") },
  { key: "aurora", style: gradient(135, "#E0F2FE", "#EDE9FE", "#FCE7F3") },
  { key: "sunset", style: gradient(135, "#FFEDD5", "#FCE7F3") },
  { key: "ocean", style: gradient(160, "#CFFAFE", "#DBEAFE") },
  { key: "dots", style: dots("#F4F5F7", "#CDD2DA") },
  { key: "grid", style: grid("#F7F8FA", "#E3E6EB") },
  { key: "night", style: solid("#1E2433"), dark: true },
  { key: "night_dots", style: dots("#1E2433", "#323B52"), dark: true },
];

export const chatWallpaperSpec = (key: string | null | undefined) =>
  CHAT_WALLPAPERS.find((w) => w.key === key) ?? CHAT_WALLPAPERS[0];
